// src/App.js
import React, { useEffect, useState, useCallback } from 'react'
import { flushSync } from 'react-dom'
import { supabase } from './supabaseClient'
import { fetchFavorites, addFavorite, removeFavorite } from './favoritesService'
import TrainDoorTransition from './components/TrainDoorTransition'
import Landing from './components/Landing'
import StationEntry from './components/StationEntry'
import Journal from './components/Journal'
import Splash from './components/Splash'
import InstallPrompt from './components/InstallPrompt'
import AuthPage from './components/AuthPage'
import Dashboard from './components/Dashboard'
import AugustTabBar from './components/AugustTabBar'
import AugustToday from './components/AugustToday'
import AugustJourney from './components/AugustJourney'
import AugustTracklist from './components/AugustTracklist'
import AugustPractices from './components/AugustPractices'
import AugustPracticeDetail from './components/AugustPracticeDetail'
import MagicRock from './components/practices/MagicRock'
import MagicalToDoList from './components/practices/MagicalToDoList'
import MagnificentOutcomes from './components/practices/MagnificentOutcomes'
import WayOutOfNegativity from './components/practices/WayOutOfNegativity'
import MoneyMagnet from './components/practices/MoneyMagnet'
import MagicMoney from './components/practices/MagicMoney'
import MagicFootsteps from './components/practices/MagicFootsteps'
import PracticeUnlockCelebration from './components/practices/PracticeUnlockCelebration'
import { PRACTICES } from './data/content'
import './App.css'
import './cities.css'

// Practice id -> the component that actually runs its flow. A Practice with no entry here
// (flow: null in PRACTICES) is unlockable/reachable but AugustPracticeDetail shows honest
// "more on this soon" copy instead of a Begin CTA — see Practices Session 1 plan.
const PRACTICE_FLOWS = { magicRock: MagicRock, magicalToDoList: MagicalToDoList, magnificentOutcomes: MagnificentOutcomes, wayOutOfNegativity: WayOutOfNegativity, moneyMagnet: MoneyMagnet, magicMoney: MagicMoney, magicFootsteps: MagicFootsteps }

// Which unlock celebrations have already been shown — local, not synced, same spirit as the
// theme-preference keys below. A practice is marked seen on either "Try It" or "Not Now", never
// on the trigger itself, so a force-quit before dismissing shows it again next time (safe default).
const UNLOCK_SEEN_KEY = 'magic_practices_unlock_seen'

const SPLASH_KEY = 'magic_splash_v2'

// August fork: hardcode the city so the selector (Landing) is bypassed at boot.
// Set back to null to restore normal city selection for comparison builds.
const DEFAULT_CITY = 'august'

// QA hatch: ?city=nyc&station=18 routes directly into that station, bypassing auth + progress gates
const _qp = new URLSearchParams(window.location.search)
const _qaCity = _qp.get('city')
const _qaStation = parseInt(_qp.get('station'), 10)
const QA_OVERRIDE = _qaCity && ['nyc','paris','london','milan','berlin','madrid','august'].includes(_qaCity) && _qaStation >= 1 && _qaStation <= 28
  ? { city: _qaCity, station: _qaStation }
  : null

export default function App() {
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  // Decided synchronously at mount, straight from sessionStorage — not inside the auth
  // callback below. Whether to show the intro has nothing to do with auth state, and coupling
  // it to onAuthStateChange's async INITIAL_SESSION event meant a first-time visitor could
  // land straight on Today if that callback fired late, more than once, or not at all (e.g.
  // React 18 dev-mode's effect double-invocation unsubscribes/resubscribes the same listener).
  const [showSplash, setShowSplash] = useState(() => {
    if (sessionStorage.getItem(SPLASH_KEY)) return false
    sessionStorage.setItem(SPLASH_KEY, '1')
    return true
  })
  const [city, setCity] = useState(QA_OVERRIDE?.city ?? DEFAULT_CITY)
  // 'home' is the august-only tab-bar shell; QA_OVERRIDE still drops straight into 'station'.
  const [view, setView] = useState(QA_OVERRIDE ? 'station' : (DEFAULT_CITY ? 'home' : 'landing')) // 'landing' | 'home' | 'station' | 'journal'
  const [augTab, setAugTab] = useState('today') // 'today' | 'journey' | 'tracklist' | 'moments' | 'profile' — august tab-bar shell only
  const [showTracklist, setShowTracklist] = useState(false) // Journey tab sub-view — august only
  // Which entry point opened the tracklist — determines where its own back arrow lands,
  // since opening it from Today requires flipping augTab to 'journey' just to render it.
  const [tracklistOrigin, setTracklistOrigin] = useState('journey') // 'journey' | 'today'
  // Set when a completed day is tapped from the tracklist, to open that specific
  // StationEntry/Journal instead of "today's" station; cleared once back at the tracklist.
  const [stationOverride, setStationOverride] = useState(null)
  const [stationReturnsTo, setStationReturnsTo] = useState(null) // null | 'tracklist' — where StationEntry/Journal's back should land
  const [theme, setTheme] = useState(() => {
    // If user has manually set a preference, respect it
    const saved = localStorage.getItem('magic_theme_manual')
    if (saved) return saved
    // Otherwise default to light — sun-based will update once geolocation resolves
    return 'sunny'
  })
  // August's own theme system — independent of the classic dark/sunny toggle above (a
  // different concept: Gold/Dark/Light vs. dark/sunny). 'gold' is the default/only mode
  // until this task added 'dark'; 'light' is planned next.
  // Default flipped gold -> dark (task: new/never-chosen sessions land on dark; anyone with an
  // explicit stored preference — this localStorage read — keeps exactly what they picked).
  const [augustTheme, setAugustTheme] = useState(() => localStorage.getItem('magic_august_theme') || 'dark')
  const [showDashboard, setShowDashboard] = useState(false)
  const [dashboardTab, setDashboardTab] = useState('overview')
  const [showMoments, setShowMoments] = useState(false)
  // cityProgress: { nyc: 2, paris: 0, london: 0, milan: 0 }
  // means nyc has 2 completed stations, so next station is 3
  const [cityProgress, setCityProgress] = useState({})
  const [round2Progress, setRound2Progress] = useState({})
  const [favoriteDays, setFavoriteDays] = useState(new Set())
  const [trainDoor, setTrainDoor] = useState(null)
  // Which Practice is open — drives both 'practiceDetail' and 'practiceFlow' views below.
  const [activePracticeId, setActivePracticeId] = useState(null)
  // The Practice currently showing its "NEW PRACTICE UNLOCKED" moment, or null. Distinct from
  // activePracticeId — the celebration is an overlay on top of the current view, not a view itself.
  const [unlockCelebration, setUnlockCelebration] = useState(null)
  const [unlockSeen, setUnlockSeen] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(UNLOCK_SEEN_KEY) || '[]')) }
    catch { return new Set() }
  })

  // Load progress for all cities once after session is available
  const loadProgress = useCallback(async (userId) => {
    const { data, error } = await supabase
      .from('journal_entries')
      .select('city, day_number, gratitudes')
      .eq('user_id', userId)
    if (!error && data) {
      const progress = {}
      data.forEach(row => {
        const hasSomething = row.gratitudes?.some(g => g?.trim())
        if (hasSomething) {
          if (!progress[row.city] || row.day_number > progress[row.city]) {
            progress[row.city] = row.day_number
          }
        }
      })
      setCityProgress(progress)
    }
  }, [])

  const loadRound2Progress = useCallback(async (userId) => {
    const { data, error } = await supabase
      .from('round_2_progress')
      .select('city, current_station')
      .eq('user_id', userId)
    if (!error && data) {
      const progress = {}
      data.forEach(row => { progress[row.city] = row.current_station })
      setRound2Progress(progress)
    }
  }, [])

  const loadFavorites = useCallback(async (userId, forCity) => {
    const { data, error } = await fetchFavorites(userId, forCity)
    if (!error) setFavoriteDays(new Set(data))
  }, [])

  useEffect(() => {
    // onAuthStateChange fires immediately (event: INITIAL_SESSION) with whatever session
    // exists at subscribe time — used as the primary auth source, not just sign-in/out events.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setLoading(false)
      if (session) loadProgress(session.user.id)
      if (session) loadRound2Progress(session.user.id)
      if (session) loadFavorites(session.user.id, city)
    })
    return () => subscription.unsubscribe()
  }, [loadProgress, loadRound2Progress, loadFavorites, city])

  // Apply theme to DOM
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  // Apply august's own theme to DOM — same single-source-of-truth pattern as the classic
  // [data-theme] above, on a separate attribute so the two systems can't collide. Only set
  // when city === 'august'; removed otherwise so it can never leak into non-august CSS.
  useEffect(() => {
    if (city === 'august') {
      document.documentElement.setAttribute('data-august-theme', augustTheme)
    } else {
      document.documentElement.removeAttribute('data-august-theme')
    }
  }, [augustTheme, city])

  const setAndPersistAugustTheme = (next) => {
    setAugustTheme(next)
    localStorage.setItem('magic_august_theme', next)
  }

  // Sun-based auto theme — runs once on mount
  useEffect(() => {
    const manual = localStorage.getItem('magic_theme_manual')
    if (manual) return // user has overridden, don't auto-switch

    if (!navigator.geolocation) return

    navigator.geolocation.getCurrentPosition(pos => {
      const { latitude: lat, longitude: lng } = pos.coords
      // Simple sunrise/sunset calculation (no API needed)
      const now = new Date()
      const d = now
      const J = 2451545.0 + (d - new Date('2000-01-01T12:00:00Z')) / 86400000
      const n = Math.ceil(J - 2451545.0 + 0.0008)
      const Js = n - lng / 360
      const M = (357.5291 + 0.98560028 * Js) % 360
      const C = 1.9148 * Math.sin(M * Math.PI/180) + 0.02 * Math.sin(2 * M * Math.PI/180)
      const lam = (M + C + 180 + 102.9372) % 360
      const Jt = 2451545.0 + Js + 0.0053 * Math.sin(M * Math.PI/180) - 0.0069 * Math.sin(2 * lam * Math.PI/180)
      const decl = Math.asin(Math.sin(lam * Math.PI/180) * Math.sin(23.4397 * Math.PI/180)) * 180/Math.PI
      const cosH = (Math.sin(-0.833 * Math.PI/180) - Math.sin(lat * Math.PI/180) * Math.sin(decl * Math.PI/180)) / (Math.cos(lat * Math.PI/180) * Math.cos(decl * Math.PI/180))
      if (cosH < -1 || cosH > 1) return // polar regions
      const H = Math.acos(cosH) * 180/Math.PI
      const Jrise = Jt - H / 360
      const Jset = Jt + H / 360
      const toDate = j => new Date((j - 2440587.5) * 86400000)
      const sunrise = toDate(Jrise)
      const sunset = toDate(Jset)
      const isDaytime = now >= sunrise && now <= sunset
      setTheme(isDaytime ? 'sunny' : 'dark')
    }, () => {
      // Geolocation denied — fall back to simple hour-based
      const h = new Date().getHours()
      setTheme(h >= 6 && h < 20 ? 'sunny' : 'dark')
    }, { timeout: 5000, maximumAge: 3600000 })
  }, [])

  // Manual toggle — saves preference, overrides sun logic
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'sunny' : 'dark'
    setTheme(next)
    localStorage.setItem('magic_theme_manual', next)
  }

  // Next station number for a city (1-based, max 28)
  const getNextStation = (c) => Math.min((cityProgress[c] || 0) + 1, 28)

  const handleSelectCity = (selectedCity) => {
    setTrainDoor(selectedCity)
  }

  const handleTrainDoorComplete = (selectedCity) => {
    setCity(selectedCity)
    setView('station')
    setTimeout(() => setTrainDoor(null), 50)
  }

  const handleBeginJourney = () => setView('journal')
  const handleNavigateToCity = (selectedCity) => {
    flushSync(() => {
      setCity(selectedCity)
      setView('station')
      setShowDashboard(false)
      setDashboardTab('overview')
    })
  }
  const handleBackToStation = () => setView('station')
  // In the august fork, DEFAULT_CITY is hardcoded — never fall through to the
  // 6-city Landing picker. Return to the Today tab-bar shell instead — unless
  // StationEntry/Journal was opened from the tracklist, in which case land back there.
  const handleBackToAugustHome = () => {
    if (DEFAULT_CITY) {
      if (stationReturnsTo === 'tracklist') {
        setStationReturnsTo(null)
        setStationOverride(null)
        setShowTracklist(true)
      }
      setView('home')
      return
    }
    setCity(null)
    setView('landing')
  }

  // Collapsed daily flow: "Write 10 Gratitudes" now opens today's writing screen directly —
  // same state a fresh entry always used (stationOverride/stationReturnsTo cleared, "back"
  // lands on Today), it just no longer detours through the tracklist-as-orientation-step and
  // StationEntry's "Track intro" screen first. See handleSeeAllDays for the old tracklist path,
  // now an explicit secondary link instead of a mandatory stop.
  const handleOpenTodayStation = () => {
    setStationOverride(null)
    setStationReturnsTo(null)
    setView('journal')
  }

  // "See all days" — Today's quiet secondary link into the tracklist (optional orientation,
  // not a gate). Tapping the current/highlighted track from there still goes through
  // handleOpenTrackDay as before.
  const handleSeeAllDays = () => {
    setTracklistOrigin('today')
    setAugTab('journey')
    setShowTracklist(true)
  }

  // Bridge from the tracklist — either the current (highlighted) track or an already-completed one.
  const handleOpenTrackDay = (dayNumber) => {
    const isCurrentDay = dayNumber === getNextStation(city)
    if (isCurrentDay) {
      // Same as the classic "write today's entry" flow — fresh entry, back lands on Today.
      // augTab must be reset explicitly: it was flipped to 'journey' just to render the
      // tracklist, and nothing else on this path would put it back.
      setStationOverride(null)
      setStationReturnsTo(null)
      setAugTab('today')
    } else {
      // Revisiting a completed day — unchanged practice-mode behavior, back returns to the tracklist.
      setStationOverride(dayNumber)
      setStationReturnsTo('tracklist')
    }
    setShowTracklist(false)
    setView('station')
  }

  // Tracklist's own back arrow — origin-aware: Journey's link always returns to Journey,
  // but Today's Start Writing needs augTab reset back since opening the tracklist flipped it.
  const handleTracklistBack = () => {
    setShowTracklist(false)
    if (tracklistOrigin === 'today') setAugTab('today')
  }

  // Journey's tracklist sub-view resets on any tab switch, so Journey stays the tab's default view.
  const handleAugTabChange = (tab) => {
    setShowTracklist(false)
    setAugTab(tab)
  }

  // StationEntry's tab bar — tapping a tab from here exits back to the home shell with that
  // tab active, same as a normal tab tap. Same reset as handleAugTabChange, plus leaving 'station'.
  const handleStationTabChange = (tab) => {
    setShowTracklist(false)
    setAugTab(tab)
    setView('home')
  }

  // Practices — open a Practice's intro screen from the library, begin its interactive flow
  // (if it has one), and exit back to the Practices tab. Mirrors the flat "back always lands
  // on the shell" convention the rest of the app uses (handleBackToAugustHome et al.) rather
  // than a stack — practiceFlow's back/complete goes straight home, not back to practiceDetail.
  const handleOpenPractice = (practiceId) => {
    setActivePracticeId(practiceId)
    setView('practiceDetail')
  }
  const handleBeginPracticeFlow = () => setView('practiceFlow')
  const handleExitPractice = () => {
    setActivePracticeId(null)
    setAugTab('practices')
    setView('home')
  }

  // The old full-screen Dashboard ("All Lines" journey tab, dashboard overview, moments)
  // is unstyled for august outside the tab-bar-embedded moments/favorites/about views —
  // redirect into the tab-bar shell instead of opening it. Other cities unaffected.
  const handleShowLines = () => {
    if (city === 'august') { setShowTracklist(false); setAugTab('journey'); setView('home'); return }
    setDashboardTab('journey'); setShowDashboard(true)
  }
  const handleShowDashboard = () => {
    if (city === 'august') { setAugTab('profile'); setView('home'); return }
    setShowDashboard(true)
  }
  const handleShowMoments = () => {
    if (city === 'august') { setAugTab('moments'); setView('home'); return }
    setShowMoments(true)
  }

  // After journal saves an entry, refresh progress
  const handleProgressUpdate = useCallback((updatedCity, stationNum) => {
    setCityProgress(prev => ({
      ...prev,
      [updatedCity]: Math.max(prev[updatedCity] || 0, stationNum)
    }))
    // Practices are august-only today. Only a built Practice (flow !== null) whose unlockDay is
    // exactly the day just completed, and not already acknowledged, triggers the celebration —
    // Magic Check (flow: null) is correctly never celebrated, since it has no screen yet.
    if (updatedCity === 'august') {
      const justUnlocked = PRACTICES.find(p => p.unlockDay === stationNum && p.flow && !unlockSeen.has(p.id))
      if (justUnlocked) setUnlockCelebration(justUnlocked)
    }
  }, [unlockSeen])

  const markUnlockSeen = (practiceId) => {
    setUnlockSeen(prev => {
      const next = new Set(prev)
      next.add(practiceId)
      localStorage.setItem(UNLOCK_SEEN_KEY, JSON.stringify([...next]))
      return next
    })
  }
  const handleTryPracticeFromUnlock = () => {
    const practiceId = unlockCelebration.id
    markUnlockSeen(practiceId)
    setUnlockCelebration(null)
    handleOpenPractice(practiceId)
  }
  const handleDismissUnlockCelebration = () => {
    markUnlockSeen(unlockCelebration.id)
    setUnlockCelebration(null)
  }

  const handleRound2ProgressUpdate = useCallback((updatedCity, stationNum) => {
    setRound2Progress(prev => ({
      ...prev,
      [updatedCity]: Math.max(prev[updatedCity] || 0, stationNum)
    }))
  }, [])

  // Optimistic toggle — updates local state immediately, persists in the background.
  const toggleFavorite = useCallback((dayNumber) => {
    if (!session) return
    const isFavorited = favoriteDays.has(dayNumber)
    setFavoriteDays(prev => {
      const next = new Set(prev)
      if (isFavorited) next.delete(dayNumber); else next.add(dayNumber)
      return next
    })
    const persist = isFavorited ? removeFavorite : addFavorite
    persist(session.user.id, city, dayNumber).then(({ error }) => {
      if (error) {
        // Roll back on failure
        setFavoriteDays(prev => {
          const next = new Set(prev)
          if (isFavorited) next.add(dayNumber); else next.delete(dayNumber)
          return next
        })
      }
    })
  }, [session, city, favoriteDays])


  // App-level train door overlay — sits above all views
  const trainDoorOverlay = trainDoor ? (
    <TrainDoorTransition
      targetCity={trainDoor}
      onComplete={() => handleTrainDoorComplete(trainDoor)}
    />
  ) : null

  // This is the very first thing ever rendered (before session/city context settles) — for
  // august it must already carry data-city so --bg/--text resolve warm from frame one.
  // Without it, this screen shows the default (non-august) theme every reload, including
  // post-OAuth-redirect reloads — the "flash of the classic look" before the app appears.
  if (loading) return (
    <div data-city={city} style={{ position: 'fixed', inset: 0, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontFamily: city === 'august' ? 'var(--font-display)' : 'var(--fn)', fontSize: 'clamp(3.5rem, 12vw, 6rem)', fontWeight: 900, letterSpacing: '-3px', lineHeight: 1, color: 'var(--text)' }}>
        {city === 'august' ? 'Magic' : 'The Magic'}
      </div>
    </div>
  )

  if (showSplash) return (
    <Splash onDone={() => setShowSplash(false)} theme={theme} city={city} augustTheme={augustTheme} onAugustThemeChange={setAndPersistAugustTheme} />
  )

  if (!session && !QA_OVERRIDE) return <>{trainDoorOverlay}<AuthPage theme={theme} toggleTheme={toggleTheme} isAugust={!!DEFAULT_CITY} /></>

  if (showMoments) return (
    <Dashboard
      session={session}
      city={city || 'nyc'}
      onBack={() => setShowMoments(false)}
      theme={theme}
      toggleTheme={toggleTheme}
      initialTab="moments"
    />
  )

  if (showDashboard) return (
    <Dashboard
      session={session}
      city={city || 'nyc'}
      onBack={() => setShowDashboard(false)}
      onNavigateToCity={handleNavigateToCity}
      theme={theme}
      toggleTheme={toggleTheme}
      initialTab={dashboardTab}
    />
  )

  if (view === 'home' && city) return (
    <div data-city={city} style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 'calc(70px + env(safe-area-inset-bottom, 0px))' }}>
        {augTab === 'today' && (
          <AugustToday
            nextStation={getNextStation(city)}
            completedCount={cityProgress[city] || 0}
            onOpenStation={handleOpenTodayStation}
            onSeeAllDays={handleSeeAllDays}
            onOpenPractice={handleOpenPractice}
            augustTheme={augustTheme} onAugustThemeChange={setAndPersistAugustTheme}
          />
        )}
        {augTab === 'journey' && showTracklist && (
          <AugustTracklist
            nextStation={getNextStation(city)}
            completedCount={cityProgress[city] || 0}
            onOpenDay={handleOpenTrackDay}
            onBack={handleTracklistBack}
            favoriteDays={favoriteDays}
            onToggleFavorite={toggleFavorite}
            augustTheme={augustTheme} onAugustThemeChange={setAndPersistAugustTheme}
          />
        )}
        {augTab === 'journey' && !showTracklist && (
          <AugustJourney
            nextStation={getNextStation(city)}
            completedCount={cityProgress[city] || 0}
            onOpenTracklist={() => { setTracklistOrigin('journey'); setShowTracklist(true) }}
            augustTheme={augustTheme} onAugustThemeChange={setAndPersistAugustTheme}
          />
        )}
        {augTab === 'practices' && (
          <AugustPractices
            completedCount={cityProgress[city] || 0}
            onOpenPractice={handleOpenPractice}
            augustTheme={augustTheme} onAugustThemeChange={setAndPersistAugustTheme}
          />
        )}
        {augTab === 'moments' && (
          <Dashboard
            session={session} city={city}
            onBack={() => setAugTab('today')}
            theme={theme} toggleTheme={toggleTheme}
            initialTab="moments" hideTabStrip
            augustTheme={augustTheme} onAugustThemeChange={setAndPersistAugustTheme}
          />
        )}
        {augTab === 'profile' && (
          <Dashboard
            session={session} city={city}
            onBack={() => setAugTab('today')}
            theme={theme} toggleTheme={toggleTheme}
            initialTab="about" hideTabStrip
            augustTheme={augustTheme} onAugustThemeChange={setAndPersistAugustTheme}
          />
        )}
      </div>
      <AugustTabBar activeTab={augTab} onChange={handleAugTabChange} />
    </div>
  )

  if (view === 'station' && city) return (
    <>
      {trainDoorOverlay}
      <StationEntry
        city={city}
        session={session}
        theme={theme}
        toggleTheme={toggleTheme}
        nextStation={stationOverride ?? (QA_OVERRIDE?.station ?? getNextStation(city))}
        cityProgress={cityProgress[city] || 0}
        onBegin={handleBeginJourney}
        onBack={handleBackToAugustHome}
        onShowLines={handleShowLines}
        skipMap={!!QA_OVERRIDE || stationOverride != null || city === 'august'}
        isFavorited={favoriteDays.has(stationOverride ?? (QA_OVERRIDE?.station ?? getNextStation(city)))}
        onToggleFavorite={toggleFavorite}
        activeTab={stationReturnsTo === 'tracklist' ? 'journey' : 'today'}
        onChangeTab={handleStationTabChange}
        augustTheme={augustTheme} onAugustThemeChange={setAndPersistAugustTheme}
      />
    </>
  )

  if (view === 'journal' && city) {
    const qaAlreadyDone = QA_OVERRIDE && session &&
      QA_OVERRIDE.station <= (cityProgress[QA_OVERRIDE.city] || 0)
    // Revisiting a completed day from the tracklist — same "already done" treatment as the
    // QA hatch: 'practice' mode appends to practice_sessions instead of overwriting the
    // original journal_entries row (first_pass doesn't pre-fill, so it would blank-and-overwrite).
    const overrideAlreadyDone = stationOverride != null && session &&
      stationOverride <= (cityProgress[city] || 0)
    const journalMode = (qaAlreadyDone || overrideAlreadyDone) ? 'practice' : 'first_pass'
    return (
      <>
        <Journal
          session={session}
          city={city}
          theme={theme}
          toggleTheme={toggleTheme}
          augustTheme={augustTheme}
          onAugustThemeChange={setAndPersistAugustTheme}
          onBack={handleBackToAugustHome}
          onShowDashboard={handleShowDashboard}
          onShowLines={handleShowLines}
          onShowMoments={handleShowMoments}
          onProgressUpdate={handleProgressUpdate}
          onRound2ProgressUpdate={handleRound2ProgressUpdate}
          journalMode={journalMode}
          qaStation={stationOverride ?? QA_OVERRIDE?.station}
        />
        {unlockCelebration && (
          <PracticeUnlockCelebration
            practice={unlockCelebration}
            onTryIt={handleTryPracticeFromUnlock}
            onNotNow={handleDismissUnlockCelebration}
          />
        )}
      </>
    )
  }

  if (view === 'practiceDetail' && activePracticeId) {
    const activePractice = PRACTICES.find(p => p.id === activePracticeId)
    return (
      <AugustPracticeDetail
        practice={activePractice}
        canBegin={!!PRACTICE_FLOWS[activePractice?.flow]}
        onBegin={handleBeginPracticeFlow}
        onBack={handleExitPractice}
        activeTab="practices"
        onChangeTab={handleStationTabChange}
        augustTheme={augustTheme} onAugustThemeChange={setAndPersistAugustTheme}
      />
    )
  }

  if (view === 'practiceFlow' && activePracticeId) {
    const activePractice = PRACTICES.find(p => p.id === activePracticeId)
    const ActiveFlow = PRACTICE_FLOWS[activePractice?.flow]
    if (!ActiveFlow) { handleExitPractice(); return null }
    return (
      <ActiveFlow
        practice={activePractice}
        session={session}
        city={city}
        onComplete={handleExitPractice}
        onBack={handleExitPractice}
        augustTheme={augustTheme} onAugustThemeChange={setAndPersistAugustTheme}
      />
    )
  }

  return (
    <>
      {trainDoorOverlay}
      <InstallPrompt />
      <Landing
        session={session}
        theme={theme}
        toggleTheme={toggleTheme}
        onSelectCity={handleSelectCity}
        cityProgress={cityProgress}
        getNextStation={getNextStation}
        onShowDashboard={() => setShowDashboard(true)}
        onShowMoments={() => setShowMoments(true)}
      />
    </>
  )
}
